import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRight,
  CheckCircle2,
  KeyRound,
  LoaderCircle,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.3 0 6.2 1.1 8.5 3.3l6.3-6.3C34.9 2.9 29.8 1 24 1 14.6 1 6.5 6.4 2.6 14.3l7.6 5.9C12.3 13.8 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 2.8-2.2 5.1-4.6 6.7l7.1 5.5c4.2-3.9 6.3-9.6 6.3-16.2z" />
    <path fill="#FBBC05" d="M10.2 28.7c-.5-1.4-.7-3-.7-4.7s.3-3.3.7-4.7l-7.6-5.9C1 16.5 0 20.2 0 24s1 7.5 2.6 10.6l7.6-5.9z" />
    <path fill="#34A853" d="M24 47c6.5 0 11.9-2.1 15.9-5.8l-7.1-5.5c-2 1.3-4.6 2.1-8.8 2.1-6.3 0-11.7-4.3-13.6-10.1l-7.6 5.9C6.5 41.6 14.6 47 24 47z" />
  </svg>
);

const normalizeError = async (response, fallback) => {
  try {
    const payload = await response.json();
    const detail = String(payload?.detail || '').trim();
    if (detail) return detail;
  } catch (_err) {
    // no-op
  }
  return fallback;
};

const formatLoginError = (value) => {
  const normalized = String(value || '').trim();
  const messages = {
    access_denied: 'Google sign-in was cancelled or access was denied.',
    missing_oauth_params: 'Google sign-in could not be completed. Please try again.',
    invalid_state: 'Your sign-in session expired. Please start again.',
    oauth_failed: 'Google sign-in was unsuccessful. Please try again.',
    mfa_setup_required: 'Multi-factor authentication must be configured before this account can continue. Contact your administrator.',
  };
  return messages[normalized] || normalized;
};

export const LoginModal = ({
  isOpen,
  error,
  onClose,
  mfaRequired = false,
  onMfaVerified,
}) => {
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [submittingMfa, setSubmittingMfa] = useState(false);
  const [lastAutoSubmittedCode, setLastAutoSubmittedCode] = useState('');
  const closeButtonRef = useRef(null);
  const dialogRef = useRef(null);
  const mfaInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setMfaCode('');
    setMfaError('');
    setSubmittingMfa(false);
    setLastAutoSubmittedCode('');
  }, [isOpen, mfaRequired]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previouslyFocusedElement = document.activeElement;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusableElements = Array.from(dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      ));
      if (focusableElements.length === 0) return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };
    const focusFrame = window.requestAnimationFrame(() => {
      if (mfaRequired) {
        mfaInputRef.current?.focus();
      } else {
        closeButtonRef.current?.focus();
      }
    });

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocusedElement instanceof HTMLElement) {
        previouslyFocusedElement.focus();
      }
    };
  }, [isOpen, mfaRequired, onClose]);

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}${API_ENDPOINTS.AUTH_GOOGLE_START}`;
  };

  const handleMfaVerify = useCallback(async ({ codeOverride = '', source = 'manual' } = {}) => {
    if (submittingMfa) return;
    const rawValue = String(codeOverride || mfaCode || '');
    const trimmedValue = rawValue.trim();
    const digitsOnly = trimmedValue.replace(/\D+/g, '');
    const looksLikeTotpInput = Boolean(trimmedValue) && /^[\d\s-]+$/.test(trimmedValue);
    const candidateCode = (looksLikeTotpInput && digitsOnly.length === 6) ? digitsOnly : trimmedValue;

    if (!candidateCode) {
      setMfaError('Enter the 6-digit MFA code (or a recovery code).');
      return;
    }
    if (source === 'auto' && candidateCode === lastAutoSubmittedCode) {
      return;
    }
    if (source === 'auto') {
      setLastAutoSubmittedCode(candidateCode);
    }

    setSubmittingMfa(true);
    setMfaError('');
    try {
      const response = await apiFetch(API_ENDPOINTS.AUTH_MFA_VERIFY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: candidateCode }),
      });
      if (!response.ok) {
        throw new Error(await normalizeError(response, 'Invalid MFA code'));
      }
      setMfaCode('');
      setLastAutoSubmittedCode('');
      onMfaVerified?.();
    } catch (err) {
      setMfaError(String(err?.message || 'Invalid MFA code'));
    } finally {
      setSubmittingMfa(false);
    }
  }, [mfaCode, lastAutoSubmittedCode, onMfaVerified, submittingMfa]);

  useEffect(() => {
    if (!mfaRequired || submittingMfa) return;
    const trimmedValue = mfaCode.trim();
    if (!trimmedValue || !/^[\d\s-]+$/.test(trimmedValue)) return;
    const digitsOnly = trimmedValue.replace(/\D+/g, '');
    if (digitsOnly.length !== 6) return;
    handleMfaVerify({ codeOverride: digitsOnly, source: 'auto' });
  }, [mfaCode, mfaRequired, submittingMfa, handleMfaVerify]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const activeError = formatLoginError(mfaRequired ? mfaError : error);
  const title = mfaRequired ? 'Verify it’s you' : 'Welcome back';
  const description = mfaRequired
    ? 'Enter your authenticator code to finish signing in securely.'
    : 'Continue with your approved Google Workspace account.';

  const modal = (
    <div
      className="modal-overlay login-modal-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className="login-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
        aria-describedby="login-modal-description"
      >
        <aside className="login-modal-brand" aria-label="TestSuite AI secure access">
          <div className="login-modal-brand-grid" aria-hidden="true" />
          <div className="login-modal-brand-orb login-modal-brand-orb-one" aria-hidden="true" />
          <div className="login-modal-brand-orb login-modal-brand-orb-two" aria-hidden="true" />

          <div className="login-modal-brand-content">
            <div className="login-modal-logo" aria-hidden="true">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="login-modal-brand-kicker">Secure testing hub</p>
              <h2>Welcome to TestSuite AI</h2>
              <p className="login-modal-brand-copy">
                Your private space for turning product requirements into
                dependable test coverage.
              </p>
            </div>
          </div>

          <ul className="login-modal-feature-list" aria-label="Access protections">
            <li>
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Invite-only application access
            </li>
            <li>
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Approved Google accounts only
            </li>
          </ul>

          <div className="login-modal-brand-footer">
            <span className="login-modal-brand-status" aria-hidden="true">
              <span />
            </span>
            <div>
              <p>Protected by MetaZ</p>
              <span>Enterprise account security</span>
            </div>
          </div>
        </aside>

        <section className="login-modal-main">
          <div className="login-modal-main-glow" aria-hidden="true" />

          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="login-modal-close"
            aria-label="Close login dialog"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="login-modal-mobile-badge">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Invite-only access
          </div>

          <div className="login-modal-heading">
            <div className="login-modal-heading-icon" aria-hidden="true">
              {mfaRequired
                ? <KeyRound className="h-5 w-5" />
                : <ShieldCheck className="h-5 w-5" />}
            </div>
            <div>
              <p>TestSuite AI account</p>
              <h3 id="login-modal-title">{title}</h3>
            </div>
          </div>

          <p id="login-modal-description" className="login-modal-description">
            {description}
          </p>

          {activeError && (
            <div className="login-modal-error" role="alert">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              <span>{activeError}</span>
            </div>
          )}

          {mfaRequired ? (
            <form
              className="login-modal-form"
              onSubmit={(event) => {
                event.preventDefault();
                handleMfaVerify({ source: 'manual' });
              }}
            >
              <label htmlFor="login-mfa-code" className="login-modal-label">
                Authenticator or recovery code
              </label>
              <input
                ref={mfaInputRef}
                id="login-mfa-code"
                type="text"
                value={mfaCode}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setMfaCode(nextValue);
                  if (nextValue.replace(/\D+/g, '') !== lastAutoSubmittedCode) {
                    setLastAutoSubmittedCode('');
                  }
                }}
                placeholder="Enter your code"
                className="login-modal-input"
                autoComplete="one-time-code"
                inputMode="text"
                disabled={submittingMfa}
              />
              <p className="login-modal-input-hint">
                Six-digit codes verify automatically. Recovery codes can be
                submitted with the button below.
              </p>

              <button
                type="submit"
                disabled={submittingMfa}
                className="login-modal-submit"
              >
                {submittingMfa ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Verifying code
                  </>
                ) : (
                  <>
                    Verify and continue
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={submittingMfa}
                className="login-modal-restart"
              >
                Restart Google sign-in
              </button>
            </form>
          ) : (
            <>
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="login-modal-google-button"
              >
                <span className="login-modal-google-icon">
                  <GoogleIcon />
                </span>
                <span>Continue with Google</span>
                <ArrowRight className="login-modal-button-arrow h-4 w-4" aria-hidden="true" />
              </button>

              <div className="login-modal-divider" aria-hidden="true">
                <span />
                <p>Secure authentication</p>
                <span />
              </div>

              <div className="login-modal-notice">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                <p>
                  Only approved accounts can continue.
                </p>
              </div>
            </>
          )}

          <p className="login-modal-help">
            Need access? Ask your administrator to invite your email.
          </p>
        </section>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
